import type { TransportBatchConfig, TransportSenderConfig } from '@/config';
import type { PixelLogger } from '@/logger';

import type { CompressionStrategy } from '../compression/types';
import { HttpSender } from './HttpSender';
import { TestSender } from './TestSender';
import type { EventPayloadBuilder } from './EventPayloadBuilder';
import {
  resolveExternalSenderConfig,
  resolveInternalSenderConfig,
} from './endpoint-resolver';
import type { EventSender } from './types';

export type EventSenderMode = 'test' | 'internal' | 'external';

export type EventSenderFactoryOptions = {
  readonly senderConfig: TransportSenderConfig;
  readonly batchConfig: TransportBatchConfig;
  readonly timeoutMs: number;
  readonly payloadBuilder: EventPayloadBuilder;
  readonly compression: CompressionStrategy;
  readonly logger: PixelLogger;
};

export type EventSenderFactoryResult = {
  readonly sender: EventSender;
  readonly mode: EventSenderMode;
  readonly testSender: TestSender | null;
  readonly httpSender: HttpSender | null;
};

export function createEventSender(
  options: EventSenderFactoryOptions
): EventSenderFactoryResult {
  const { senderConfig } = options;

  if (senderConfig.mode === 'test') {
    const sender = TestSender.fromConfig(options.logger, senderConfig);
    return { sender, mode: 'test', testSender: sender, httpSender: null };
  }

  const resolved =
    senderConfig.mode === 'internal'
      ? resolveInternalSenderConfig(senderConfig, options.timeoutMs)
      : resolveExternalSenderConfig(
          senderConfig,
          options.batchConfig,
          options.timeoutMs
        );
  const sender = new HttpSender(
    resolved,
    options.payloadBuilder,
    options.compression,
    options.logger
  );

  return {
    sender,
    mode: senderConfig.mode,
    testSender: null,
    httpSender: sender,
  };
}
